import React, { useState, useCallback, useMemo } from "react"
import { Switch, Dropdown, Menu } from "antd"
import PropTypes from "prop-types"
import styles from "./index.module.css"
import DataTable from "ui/DataTable"
import DataTableResponsive from "ui/DataTableResponsive"
import { getSortParams } from "../getSortParams"
import CustomPagination from "components/common/CustomPagination"
import { DocumentsAddedModal } from "../modal/DocumentsAdded"
import DeleteConfirmation from "../Deleteconfirmation"
import WaiverAssignedExperiencesModal from "../modal/WaiverAssignedExperiencesModal"
import { useTranslation } from "react-i18next"
import { updateDigitalWaiverStatus, getGuideDigitalWaivers, deleteDigitalWaiver } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { MoreOutlined } from "@ant-design/icons"
import { useDevice } from "hooks/useDevice"
const DocumentsAddedTable = ({
  data,
  metadata = {},
  currentPage,
  setCurrentPage,
  onView,
  onEdit, // Add onEdit prop
  sortState,
  setSortState
}) => {
  const dispatch = useDispatch()
  const { isMobile, isTablet } = useDevice()
  const [pageSize, setPageSize] = useState(10)
  // const [sortState, setSortState] = useState({ sort: '', order: '' })
  const [showModal, setShowModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [isView, setIsView] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteRecord, setDeleteRecord] = useState(null)
  const [isViewLoading, setIsViewLoading] = useState(false)
  const [showExperiencesModal, setShowExperiencesModal] = useState(false)
  const [experiencesRecord, setExperiencesRecord] = useState(null)
  const { deleteLoading } = useSelector((state) => state.digitalWaiver)
  const { t } = useTranslation()
  const isCompact = isMobile || isTablet
  const handleView = useCallback(
    (record) => {
      if (onView) {
        onView(record)
        return
      }

      setSelectedRecord(record)
      setShowModal(true)
      setIsViewLoading(true)
      setIsView(true)
    },
    [onView]
  )

  const handleEdit = useCallback(
    (record) => {
      if (onEdit) {
        onEdit(record)
      }
    },
    [onEdit]
  )
  const handleDelete = useCallback((record) => {
    setDeleteRecord(record)
    setShowDeleteModal(true)
  }, [])

  const handleDeleteConfirm = () => {
    dispatch(deleteDigitalWaiver(deleteRecord.id))
      .then((res) => {
        toaster.success(res?.message)
        dispatch(getGuideDigitalWaivers(1, pageSize)) // Refresh data
        setCurrentPage(1)
        setShowDeleteModal(false)
        setDeleteRecord(null)
      })
      .catch((error) => {
        console.error("Failed to delete waiver:", error)
      })
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
    setDeleteRecord(null)
  }

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page)
    },
    [setCurrentPage]
  )

  const handlePageSizeChange = useCallback(
    (size) => {
      setPageSize(size)
      setCurrentPage(1)
    },
    [setCurrentPage]
  )
  const handleChangeStatus = useCallback(
    (record, isActive) => {
      const formData = new FormData()
      formData.append("digital_waiver[is_active]", isActive)
      dispatch(updateDigitalWaiverStatus(record.id, formData))
        .then((res) => {
          toaster.success(res?.message)
          dispatch(getGuideDigitalWaivers(currentPage, pageSize)) // Refresh data
        })
        .catch((error) => {
          console.error("Failed to update status:", error)
        })
    },
    [currentPage, dispatch, pageSize]
  )

  const columns = [
    {
      title: <div className={styles.columnHeader}>Waiver</div>,
      // sorter: (a, b) => a.name.localeCompare(b.name),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => {
        const capitalizeWords = (str) => str.replaceAll(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        return (
          <button className={styles.waiverLink} onClick={() => handleView(record.rawData)} type="button" tabIndex={0}>
            {text && typeof text === "string" ? capitalizeWords(text) : text}
          </button>
        )
      }
    },
    {
      title: "Assigned Experiences",
      dataIndex: "experience_count",
      key: "experience_count",
      render: (count, record) => (
        <button
          type="button"
          className={styles.assignedExperiencesCell}
          onClick={(e) => {
            e.stopPropagation()
            setExperiencesRecord(record.rawData)
            setShowExperiencesModal(true)
          }}
        >
          {count || 0} Experiences
        </button>
      )
    },
    {
      title: <div className={styles.columnHeader}>Date Created</div>,
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      sorter: (a, b) => (a.is_active || "").toString().localeCompare((b.is_active || "").toString()),
      render: (_, record) => (
        <div className={styles.statusCell}>
          <span className={record.is_active ? styles.activeStatus : styles.inactiveStatus}>{record.is_active ? "Active" : "Inactive"}</span>
          <Switch
            defaultChecked={record.is_active}
            onChange={(e) => handleChangeStatus(record.rawData, e)}
            onClick={(e) => e.stopPropagation()}
            className={styles.statusSwitch}
          />
        </div>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="view" onClick={() => handleEdit(record.rawData)}>
                Edit
              </Menu.Item>
              <Menu.Item key="delete" danger onClick={() => handleDelete(record.rawData)}>
                Delete
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <button type="button" className={styles.actionTrigger} onClick={(e) => e.stopPropagation()}>
            ⋯
          </button>
        </Dropdown>
      )
    }
  ]

  const formattedData = useMemo(
    () =>
      data.map((item) => ({
        key: item.id?.toString(),
        name: item.name,
        is_active: item.is_active,
        document: item.document_filename || "No document",
        created_at: new Date(item.created_at).toLocaleDateString(),
        experience_count: item.experience_count,
        rawData: item
      })),
    [data]
  )

  const mobileData = useMemo(
    () =>
      formattedData.map((item) => ({
        id: item.key,
        name: item.name,
        assignedExperiences: `${item.experience_count || 0} Experiences`,
        createdAt: item.created_at,
        status: item.is_active ? "Active" : "Inactive",
        rawData: item.rawData,
        is_active: item.is_active
      })),
    [formattedData]
  )

  const getTableActions = useCallback(
    (record) => {
      if (!record?.rawData) {
        return []
      }

      return [
        {
          label: "Assigned Experiences",
          onClick: () => {
            setExperiencesRecord(record.rawData)
            setShowExperiencesModal(true)
          },
          color: "primary",
          variant: "outlined"
        },
        {
          label: record.is_active ? "Mark Inactive" : "Mark Active",
          onClick: () => handleChangeStatus(record.rawData, !record.is_active),
          color: record.is_active ? "danger" : "green",
          variant: "outlined"
        },
        {
          label: "Edit",
          onClick: () => handleEdit(record.rawData),
          color: "primary",
          variant: "outlined"
        },
        {
          label: "Delete",
          color: "danger",
          variant: "solid",
          onClick: () => handleDelete(record.rawData)
        }
      ]
    },
    [handleDelete, handleEdit, handleChangeStatus]
  )

  // Table change handler for sorting
  const handleTableChange = (pagination, filters, sorter) => {
    const { sort, order } = getSortParams(sorter)
    setSortState({ sort, order })
    setCurrentPage(1)
    dispatch(getGuideDigitalWaivers(1, pageSize, "", "", null, sort, order))
  }
  return (
    <div>
      {isCompact ? (
        <DataTableResponsive
          data={mobileData}
          onItemClick={(record) => handleView(record.rawData)}
          actionIcon={<MoreOutlined className={styles.mobileActionIcon} />}
          getTableActions={getTableActions}
          fieldOrder={["name", "assignedExperiences", "createdAt", "status"]}
          labelMap={{
            name: "Waiver",
            assignedExperiences: "Assigned Experiences",
            createdAt: "Date Created",
            status: "Status"
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          dataSource={formattedData}
          pagination={false}
          rowKey="key"
          onChange={handleTableChange}
          onRowClick={(_, record) => handleView(record.rawData)}
        />
      )}
      <div>
        <CustomPagination
          metadata={metadata}
          pageSize={pageSize}
          total={metadata?.total_results || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
      {showModal && (
        <DocumentsAddedModal
          isLoading={isViewLoading}
          setIsLoading={setIsViewLoading}
          visible={showModal}
          initialData={selectedRecord}
          onCancel={() => setShowModal(false)}
          onSave={() => setShowModal(false)}
          isView={isView}
        />
      )}
      {showExperiencesModal && (
        <WaiverAssignedExperiencesModal
          open={showExperiencesModal}
          onCancel={() => {
            setShowExperiencesModal(false)
            setExperiencesRecord(null)
          }}
          waiver={experiencesRecord}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmation
          title={t("digital_waiver_and_contracts.delete_waiver_title")}
          content={
            <span>
              {t("digital_waiver_and_contracts.delete_confirmation")} <strong>{deleteRecord?.name}?</strong>{" "}
              {t("digital_waiver_and_contracts.delete_confirmation_suffix")}
            </span>
          }
          visible={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          record={deleteRecord}
          loading={deleteLoading}
        />
      )}
    </div>
  )
}

DocumentsAddedTable.propTypes = {
  data: PropTypes.array.isRequired,
  metadata: PropTypes.shape({
    total_results: PropTypes.number,
    page: PropTypes.number,
    total_pages: PropTypes.number
  }),
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  sortState: PropTypes.shape({
    sort: PropTypes.string,
    order: PropTypes.string
  }),
  setSortState: PropTypes.func
}

DocumentsAddedTable.defaultProps = {
  metadata: {},
  onView: null,
  onEdit: null,
  sortState: { sort: "", order: "" },
  setSortState: () => {}
}

export default DocumentsAddedTable
